import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DonutLargeOutlinedIcon from "@mui/icons-material/DonutLargeOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import FindInPageOutlinedIcon from "@mui/icons-material/FindInPageOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { BarChart, PieChart } from "@mui/x-charts";
import { DataGrid } from "@mui/x-data-grid";
import { frFR } from "@mui/x-data-grid/locales";
import html2canvas from "html2canvas";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import SMQMainContainer from "../../../components/SMQMainContainer";
import UserService from "../../../services/UserService";
import ConstatService from "../../../services/smq/ConstatService";
import ProcessusService from "../../../services/smq/ProcessusService";
import { getDisplayedNcStatus } from "../NC/ncStatusHelper";
import styles, {
  COLORS,
  PRIORITY_STYLES,
  STATUS_STYLES,
} from "./Dashboad.styles";
import {
  fetchDashboardExportDetails,
  saveDashboardWorkbook,
} from "./dashboardExport";
import { Grid } from "@material-ui/core";

const EMPTY_FILTERS = {
  date_debut: "",
  date_fin: "",
  direction_id: null,
  processus_id: null,
};

const DASHBOARD_ERROR_MESSAGES = Object.freeze({
  details: "Une erreur s'est produite lors de la r\u00e9cup\u00e9ration des d\u00e9tails.",
  direction: "Une erreur s'est produite lors de la r\u00e9cup\u00e9ration de votre direction.",
  export: "Une erreur s'est produite lors de l'export du tableau de bord.",
  filters: "Une erreur s'est produite lors de la r\u00e9cup\u00e9ration des filtres.",
  overdue: "Une erreur s'est produite lors de la r\u00e9cup\u00e9ration des retards.",
  recent: "Une erreur s'est produite lors de la r\u00e9cup\u00e9ration des derni\u00e8res NC.",
  summary: "Une erreur s'est produite lors de la r\u00e9cup\u00e9ration des indicateurs.",
});

const normalizePositiveId = (value) => {
  const normalized = Number(value);
  return Number.isInteger(normalized) && normalized > 0 ? normalized : null;
};

const getStoredUserDirectionId = () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem("USER_DETAILS") || "null");
    return normalizePositiveId(userDetails?.direction_id) ||
      normalizePositiveId(localStorage.getItem("USER_DIR"));
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error("Dashboard stored user direction is invalid", error);
    }
    return normalizePositiveId(localStorage.getItem("USER_DIR"));
  }
};

const getDefaultDirectionId = (directionId) => directionId === 1 ? null : directionId;

const createInitialFilters = (directionId = null) => ({
  ...EMPTY_FILTERS,
  direction_id: directionId,
});

const logDashboardError = (context, error) => {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.error("Dashboard " + context + " failed", error);
  }
};
const EMPTY_STATS = {
  closed: 0,
  correctives_in_progress: 0,
  curatifs_in_progress: 0,
  due_soon: 0,
  expired: 0,
  in_progress: 0,
  open: 0,
  total: 0,
};

const DETAIL_DIALOGS = {
  closed: { title: "NC clôturées", type: "closed" },
  due_soon: { title: "Échéances dans les 3 prochains jours", type: "due_soon" },
  expired: { title: "NC avec délai dépassé", type: "expired" },
  open: { title: "NC non traitées", type: "open" },
};

const STATUS_COLORS = {
  done: "#188f4c",
  in_progress: "#df812b",
  open: "#df3b35",
};

const GUIDE_STEPS = [
  { color: COLORS.teal, icon: FindInPageOutlinedIcon, label: "Constat" },
  { color: COLORS.blue, icon: DescriptionOutlinedIcon, label: "Ouverture FNC" },
  { color: COLORS.orange, icon: BuildOutlinedIcon, label: "Curatif" },
  { color: COLORS.purple, icon: SearchOutlinedIcon, label: "Analyse des causes" },
  { color: COLORS.green, icon: TrackChangesOutlinedIcon, label: "Action corrective" },
  { color: COLORS.red, icon: AnalyticsOutlinedIcon, label: "Suivi efficacité" },
];

const formatDate = (value) => {
  const dateValue = String(value || "").slice(0, 10);
  if (!dateValue) return "—";
  const date = new Date(`${dateValue}T00:00:00`);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("fr-FR");
};

const displayedStatus = (row) =>
  row.statut === "done" && row.has_reopened_child
    ? "done_after_reopen"
    : getDisplayedNcStatus(row.statut, row.delai);

const prepareRow = (row) => ({
  ...row,
  id: row.constat_id,
  statut_affiche: displayedStatus(row),
});

const prepareDetailRow = (row, detailType) => {
  if (detailType === "curative") {
    return { ...row, id: `curative-${row.curatif_id}` };
  }
  if (detailType === "corrective") {
    return { ...row, id: `corrective-${row.plan_action_id}` };
  }
  return prepareRow(row);
};

const sameId = (left, right) => String(left ?? "") === String(right ?? "");

const Badge = ({ config }) => config ? (
  <Chip label={config.label} size="small" sx={styles.badge(config)} />
) : <Typography color="#8090a2">—</Typography>;

Badge.propTypes = {
  config: PropTypes.shape({
    backgroundColor: PropTypes.string,
    color: PropTypes.string,
    label: PropTypes.string,
  }),
};
Badge.defaultProps = { config: null };

const KpiCard = ({ accent, helper, helperIcon: HelperIcon, label, onClick, value }) => (
  <ButtonBase
    aria-label={`${label} : ${value}. Afficher les détails`}
    onClick={onClick}
    sx={styles.kpiButton}
  >
    <Paper elevation={0} sx={styles.kpiPaper(accent)}>
      <Typography color={accent} fontSize={30} fontWeight={900} lineHeight={1}>{value}</Typography>
      <Typography color="#536b91" fontSize={11.5} fontWeight={800} sx={styles.kpiLabel}>{label}</Typography>
      <Box sx={styles.kpiHelper(accent)}>
        <HelperIcon sx={styles.helperIcon} />
        <Typography color="inherit" fontSize={10}>{helper}</Typography>
      </Box>
    </Paper>
  </ButtonBase>
);

KpiCard.propTypes = {
  accent: PropTypes.string.isRequired,
  helper: PropTypes.string.isRequired,
  helperIcon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  value: PropTypes.number.isRequired,
};

const ActionsKpiCard = ({ correctives, curatifs, onClick }) => (
  <ButtonBase
    aria-label={`Actions en cours : ${curatifs} curatives et ${correctives} correctives. Afficher les détails`}
    onClick={onClick}
    sx={styles.kpiButton}
  >
    <Paper elevation={0} sx={styles.actionsKpiPaper}>
      <Box sx={styles.actionsKpiGrid}>
        <Box>
          <Typography color={COLORS.orange} fontSize={24} fontWeight={900} lineHeight={1}>{curatifs}</Typography>
          <Typography color="#536b91" fontSize={10.5} fontWeight={800} sx={styles.kpiLabel}>Curatifs en cours</Typography>
          <Box sx={styles.actionsKpiHelper}>
            <Typography color="inherit" fontSize={10}>Corrective à définir</Typography>
          </Box>
        </Box>
        <Box sx={styles.actionsKpiSeparator} />
        <Box>
          <Typography color={COLORS.orange} fontSize={24} fontWeight={900} lineHeight={1}>{correctives}</Typography>
          <Typography color="#536b91" fontSize={10.5} fontWeight={800} sx={styles.kpiLabel}>Correctives en cours</Typography>
          <Box sx={styles.actionsKpiHelper}>
            <Typography color="inherit" fontSize={10}>En attente de vérification</Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  </ButtonBase>
);

ActionsKpiCard.propTypes = {
  correctives: PropTypes.number.isRequired,
  curatifs: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
};

const ChartCard = ({ children, icon: Icon, title }) => (
  <Paper elevation={0} sx={styles.chartCard}>
    <Box sx={styles.chartHeader}>
      <Icon sx={styles.chartIcon} />
      <Typography color={COLORS.navy} fontSize={13} fontWeight={800}>{title}</Typography>
    </Box>
    {children}
  </Paper>
);

ChartCard.propTypes = {
  children: PropTypes.node.isRequired,
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
};

const StatusChart = ({ distribution, total }) => {
  const data = distribution.map((item) => ({
    color: STATUS_COLORS[item.status] || "#7890a8",
    id: item.status,
    label: item.label,
    value: Number(item.total) || 0,
  }));
  const hasData = data.some((item) => item.value > 0);

  return (
    <Box sx={styles.statusChart}>
      {hasData ? (
        <Box sx={styles.statusChartFigure}>
          <PieChart
            height={140}
            margin={{ bottom: 5, left: 5, right: 5, top: 5 }}
            series={[{ data, innerRadius: 31, outerRadius: 49, paddingAngle: 1 }]}
            slotProps={{ legend: { hidden: true }, tooltip: { trigger: "none" } }}
            sx={styles.statusPie}
            width={150}
          />
          <Box sx={styles.statusChartCenter}>
            <Typography color={COLORS.navy} fontSize={16} fontWeight={900}>{total}</Typography>
            <Typography color="#71849a" fontSize={8}>total</Typography>
          </Box>
        </Box>
      ) : <Typography color="#71849a" fontSize={12}>Aucune donnée</Typography>}
      <Box sx={styles.statusChartLegend}>
        {data.map((item) => (
          <Box key={item.id} sx={styles.statusChartLegendItem}>
            <Box sx={styles.statusChartLegendMarker(item.color)} />
            <Typography color="#233750" fontSize={11}>{item.label}</Typography>
            <Typography color={COLORS.navy} fontSize={11} fontWeight={800} sx={styles.statusChartLegendCount}>{item.value}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

StatusChart.propTypes = {
  distribution: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    status: PropTypes.string,
    total: PropTypes.number,
  })).isRequired,
  total: PropTypes.number.isRequired,
};

const ProcessChart = ({ data }) => (
  <Box sx={styles.processChart}>
    {data.length ? (
      <BarChart
        grid={{ horizontal: true }}
        height={185}
        margin={{ bottom: 35, left: 30, right: 10, top: 15 }}
        series={[{ color: COLORS.teal, data: data.map((item) => Number(item.total) || 0) }]}
        slotProps={{ legend: { hidden: true } }}
        xAxis={[{ data: data.map((item) => item.process), scaleType: "band", tickLabelStyle: { fontSize: 9 } }]}
        yAxis={[{ min: 0, tickMinStep: 1, tickLabelStyle: { fontSize: 9 } }]}
      />
    ) : (
      <Box sx={styles.emptyChart}>
        <Typography color="#71849a" fontSize={12}>Aucune donnée</Typography>
      </Box>
    )}
  </Box>
);

ProcessChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    process: PropTypes.string,
    total: PropTypes.number,
  })).isRequired,
};

const DashboardSkeleton = () => (
  <Box sx={styles.skeleton}>
    <Box sx={styles.skeletonGrid}>
      {Array.from({ length: 6 }, (_, index) => <Skeleton height={112} key={index} variant="rounded" />)}
    </Box>
    <Skeleton height={250} sx={styles.skeletonSection} variant="rounded" />
  </Box>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const [storedUserDirectionId] = useState(getStoredUserDirectionId);
  const requestIdsRef = useRef({ overdue: 0, recent: 0, summary: 0 });
  const directionRequestIdRef = useRef(0);
  const dialogRequestIdRef = useRef(0);
  const exportRequestIdRef = useRef(0);
  const statusChartRef = useRef(null);
  const processChartRef = useRef(null);
  const [defaultDirectionId, setDefaultDirectionId] = useState(() =>
    getDefaultDirectionId(storedUserDirectionId)
  );
  const [directionResolved, setDirectionResolved] = useState(
    storedUserDirectionId !== null
  );
  const [directionLoading, setDirectionLoading] = useState(false);
  const [directionError, setDirectionError] = useState(null);
  const [filters, setFilters] = useState(() =>
    createInitialFilters(getDefaultDirectionId(storedUserDirectionId))
  );
  const [directions, setDirections] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [optionsError, setOptionsError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [overduePreview, setOverduePreview] = useState({ rows: [], total: 0 });
  const [recentRows, setRecentRows] = useState([]);
  const [loading, setLoading] = useState({ overdue: true, recent: true, summary: true });
  const [errors, setErrors] = useState({ overdue: null, recent: null, summary: null });
  const [detailDialog, setDetailDialog] = useState(null);
  const [actionTab, setActionTab] = useState("curative");
  const [dialogRows, setDialogRows] = useState([]);
  const [dialogRowCount, setDialogRowCount] = useState(0);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [dialogPagination, setDialogPagination] = useState({ page: 0, pageSize: 10 });
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const activeDetailType = detailDialog?.type === "actions"
    ? actionTab
    : detailDialog?.type;

  const dateError = filters.date_debut && filters.date_fin &&
    filters.date_debut > filters.date_fin
    ? "La date de début doit précéder la date de fin."
    : null;

  const queryFilters = useMemo(() => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== "") params[key] = value;
    });
    return params;
  }, [filters]);

  const directionOptions = useMemo(() => directions
    .filter((direction) => direction.direction_id !== null &&
      direction.direction_id !== undefined)
    .map((direction) => ({
      code: direction.direction_code || "",
      id: direction.direction_id,
      label: [direction.direction_code, direction.direction_name]
        .filter(Boolean)
        .join(" — "),
      name: direction.direction_name || "",
    }))
    .sort((left, right) =>
      left.code.localeCompare(right.code, "fr", { sensitivity: "base" }) ||
      left.name.localeCompare(right.name, "fr", { sensitivity: "base" })
    ), [directions]);

  const processOptions = useMemo(() => processes
    .filter((process) => !filters.direction_id || sameId(process.direction_id, filters.direction_id))
    .map((process) => ({
      direction_id: process.direction_id,
      id: process.processus_id,
      label: [process.instance, process.referentiel_nom].filter(Boolean).join(" — "),
    })), [filters.direction_id, processes]);

  const selectedDirection = directionOptions.find((option) =>
    sameId(option.id, filters.direction_id)
  ) || null;
  const selectedProcess = processOptions.find((option) =>
    sameId(option.id, filters.processus_id)
  ) || null;

  const loadOptions = useCallback(async () => {
    setOptionsError(null);
    const [directionsResult, processesResult] = await Promise.allSettled([
      ProcessusService.getDirections(),
      ProcessusService.getProcessus(),
    ]);

    if (!mountedRef.current) return;

    let hasOptionError = false;
    if (directionsResult.status === "fulfilled" &&
        Array.isArray(directionsResult.value?.data)) {
      setDirections(directionsResult.value.data);
    } else {
      hasOptionError = true;
      logDashboardError(
        "directions loading",
        directionsResult.status === "rejected"
          ? directionsResult.reason
          : new Error("Invalid directions response")
      );
    }

    if (processesResult.status === "fulfilled" &&
        Array.isArray(processesResult.value?.data)) {
      setProcesses(processesResult.value.data);
    } else {
      hasOptionError = true;
      logDashboardError(
        "processes loading",
        processesResult.status === "rejected"
          ? processesResult.reason
          : new Error("Invalid processes response")
      );
    }

    setOptionsError(hasOptionError ? DASHBOARD_ERROR_MESSAGES.filters : null);
  }, []);

  const loadUserDirection = useCallback(async () => {
    const requestId = directionRequestIdRef.current + 1;
    directionRequestIdRef.current = requestId;
    setDirectionLoading(true);
    setDirectionError(null);

    try {
      const userId = normalizePositiveId(localStorage.getItem("USER"));
      if (!userId) throw new Error("Missing authenticated user id");

      const response = await UserService.getUserSummaryByUserId(userId);
      const directionId = normalizePositiveId(response?.data?.direction_id);
      if (!directionId) throw new Error("Invalid user direction response");

      if (!mountedRef.current || directionRequestIdRef.current !== requestId) return;

      const nextDefaultDirectionId = getDefaultDirectionId(directionId);
      localStorage.setItem("USER_DIR", String(directionId));
      setDefaultDirectionId(nextDefaultDirectionId);
      setFilters(createInitialFilters(nextDefaultDirectionId));
      setDirectionResolved(true);
    } catch (error) {
      logDashboardError("user direction loading", error);
      if (mountedRef.current && directionRequestIdRef.current === requestId) {
        setDirectionResolved(false);
        setDirectionError(DASHBOARD_ERROR_MESSAGES.direction);
      }
    } finally {
      if (mountedRef.current && directionRequestIdRef.current === requestId) {
        setDirectionLoading(false);
      }
    }
  }, []);

  const loadSummary = useCallback(async () => {
    if (dateError) {
      requestIdsRef.current.summary += 1;
      setLoading((current) => ({ ...current, summary: false }));
      return;
    }
    const requestId = requestIdsRef.current.summary + 1;
    requestIdsRef.current.summary = requestId;
    setLoading((current) => ({ ...current, summary: true }));
    setErrors((current) => ({ ...current, summary: null }));
    try {
      const response = await ConstatService.getDashboard(queryFilters);
      const data = response?.data;
      if (!data?.stats || !Array.isArray(data.status_distribution) ||
          !Array.isArray(data.process_distribution)) {
        throw new Error("La réponse des indicateurs est invalide.");
      }
      if (mountedRef.current && requestIdsRef.current.summary === requestId) {
        setSummary(data);
      }
    } catch (requestError) {
      logDashboardError("summary loading", requestError);
      if (mountedRef.current && requestIdsRef.current.summary === requestId) {
        setErrors((current) => ({
          ...current,
          summary: DASHBOARD_ERROR_MESSAGES.summary,
        }));
      }
    } finally {
      if (mountedRef.current && requestIdsRef.current.summary === requestId) {
        setLoading((current) => ({ ...current, summary: false }));
      }
    }
  }, [dateError, queryFilters]);

  const loadOverduePreview = useCallback(async () => {
    if (dateError) {
      requestIdsRef.current.overdue += 1;
      setLoading((current) => ({ ...current, overdue: false }));
      return;
    }
    const requestId = requestIdsRef.current.overdue + 1;
    requestIdsRef.current.overdue = requestId;
    setLoading((current) => ({ ...current, overdue: true }));
    setErrors((current) => ({ ...current, overdue: null }));
    try {
      const response = await ConstatService.getDashboardOverdue({
        ...queryFilters,
        page: 1,
        page_size: 8,
      });
      const data = response?.data;
      if (!Array.isArray(data?.rows) || !data?.pagination) {
        throw new Error("La réponse des échéances est invalide.");
      }
      if (mountedRef.current && requestIdsRef.current.overdue === requestId) {
        setOverduePreview({
          rows: data.rows.map(prepareRow),
          total: Number(data.pagination.total) || 0,
        });
      }
    } catch (requestError) {
      logDashboardError("overdue loading", requestError);
      if (mountedRef.current && requestIdsRef.current.overdue === requestId) {
        setErrors((current) => ({
          ...current,
          overdue: DASHBOARD_ERROR_MESSAGES.overdue,
        }));
      }
    } finally {
      if (mountedRef.current && requestIdsRef.current.overdue === requestId) {
        setLoading((current) => ({ ...current, overdue: false }));
      }
    }
  }, [dateError, queryFilters]);

  const loadRecent = useCallback(async () => {
    if (dateError) {
      requestIdsRef.current.recent += 1;
      setLoading((current) => ({ ...current, recent: false }));
      return;
    }
    const requestId = requestIdsRef.current.recent + 1;
    requestIdsRef.current.recent = requestId;
    setLoading((current) => ({ ...current, recent: true }));
    setErrors((current) => ({ ...current, recent: null }));
    try {
      const response = await ConstatService.getDashboardRecent(queryFilters);
      if (!Array.isArray(response?.data)) {
        throw new Error("La réponse des dernières NC est invalide.");
      }
      if (mountedRef.current && requestIdsRef.current.recent === requestId) {
        setRecentRows(response.data.map(prepareRow));
      }
    } catch (requestError) {
      logDashboardError("recent NC loading", requestError);
      if (mountedRef.current && requestIdsRef.current.recent === requestId) {
        setErrors((current) => ({
          ...current,
          recent: DASHBOARD_ERROR_MESSAGES.recent,
        }));
      }
    } finally {
      if (mountedRef.current && requestIdsRef.current.recent === requestId) {
        setLoading((current) => ({ ...current, recent: false }));
      }
    }
  }, [dateError, queryFilters]);

  const loadDialogRows = useCallback(async () => {
    if (!detailDialog || !activeDetailType || dateError) {
      dialogRequestIdRef.current += 1;
      setDialogLoading(false);
      return;
    }
    const requestId = dialogRequestIdRef.current + 1;
    dialogRequestIdRef.current = requestId;
    setDialogLoading(true);
    setDialogError(null);
    try {
      const response = await ConstatService.getDashboardDetails(activeDetailType, {
        ...queryFilters,
        page: dialogPagination.page + 1,
        page_size: dialogPagination.pageSize,
      });
      const data = response?.data;
      if (!Array.isArray(data?.rows) || !data?.pagination) {
        throw new Error("La réponse des détails du tableau de bord est invalide.");
      }
      if (mountedRef.current && dialogRequestIdRef.current === requestId) {
        setDialogRows(data.rows.map((row) => prepareDetailRow(row, activeDetailType)));
        setDialogRowCount(Number(data.pagination.total) || 0);
      }
    } catch (requestError) {
      logDashboardError("details loading", requestError);
      if (mountedRef.current && dialogRequestIdRef.current === requestId) {
        setDialogRows([]);
        setDialogRowCount(0);
        setDialogError(DASHBOARD_ERROR_MESSAGES.details);
      }
    } finally {
      if (mountedRef.current && dialogRequestIdRef.current === requestId) {
        setDialogLoading(false);
      }
    }
  }, [activeDetailType, dateError, detailDialog, dialogPagination, queryFilters]);

  useEffect(() => {
    mountedRef.current = true;
    loadOptions();
    return () => {
      mountedRef.current = false;
      requestIdsRef.current = { overdue: 0, recent: 0, summary: 0 };
      directionRequestIdRef.current += 1;
      dialogRequestIdRef.current += 1;
      exportRequestIdRef.current += 1;
    };
  }, [loadOptions]);

  useEffect(() => {
    if (!directionResolved && !directionLoading && !directionError) {
      loadUserDirection();
    }
  }, [directionError, directionLoading, directionResolved, loadUserDirection]);

  useEffect(() => {
    if (!directionResolved) return;
    loadSummary();
    loadOverduePreview();
    loadRecent();
  }, [directionResolved, loadOverduePreview, loadRecent, loadSummary]);

  useEffect(() => {
    setDialogPagination((current) => ({ ...current, page: 0 }));
  }, [activeDetailType, queryFilters]);

  useEffect(() => {
    loadDialogRows();
  }, [loadDialogRows]);

  const columns = useMemo(() => [
    {
      field: "reference",
      flex: 1,
      headerName: "Code NC",
      minWidth: 175,
      renderCell: ({ row, value }) => (
        <Box sx={styles.referenceCell}>
          <Typography color={COLORS.navy} fontFamily="DM Mono, monospace" fontSize={11} fontWeight={800}>{value || "—"}</Typography>
          {row.statut_affiche === "delai_depasse" && <WarningAmberOutlinedIcon sx={styles.warningIcon} />}
        </Box>
      ),
    },
    { field: "constat_date", headerName: "Date", minWidth: 110, renderCell: ({ value }) => formatDate(value) },
    { field: "processus", flex: 0.9, headerName: "Processus", minWidth: 130 },
    { field: "description_constat", flex: 1.8, headerName: "Description du constat", minWidth: 250 },
    {
      field: "criticite",
      headerName: "Priorité",
      minWidth: 125,
      renderCell: ({ value }) => <Badge config={PRIORITY_STYLES[String(value || "").toLowerCase()]} />,
    },
    {
      field: "statut_affiche",
      headerName: "Statut",
      minWidth: 180,
      renderCell: ({ value }) => <Badge config={STATUS_STYLES[value]} />,
    },
  ], []);

  const ncDetailColumns = useMemo(() => [
    ...columns.slice(0, 3),
    { field: "direction_code", headerName: "Direction", minWidth: 105 },
    columns[3],
    columns[4],
    {
      field: "delai",
      headerName: "Échéance",
      minWidth: 120,
      renderCell: ({ row, value }) => (
        <Typography
          color={row.statut_affiche === "delai_depasse" ? COLORS.red : COLORS.navy}
          fontSize={11.5}
          fontWeight={row.statut_affiche === "delai_depasse" ? 700 : 500}
        >
          {formatDate(value)}
        </Typography>
      ),
    },
    columns[5],
  ], [columns]);

  const curativeColumns = useMemo(() => [
    { field: "reference", flex: 1, headerName: "Référence NC", minWidth: 175 },
    { field: "processus", flex: 1, headerName: "Processus", minWidth: 140 },
    { field: "direction_code", headerName: "Direction", minWidth: 105 },
    { field: "description", flex: 1.8, headerName: "Action curative", minWidth: 260 },
    { field: "responsable_name", flex: 1, headerName: "Responsable", minWidth: 180 },
    {
      field: "date_realisation",
      headerName: "Date de réalisation",
      minWidth: 150,
      renderCell: ({ value }) => formatDate(value),
    },
    {
      field: "statut",
      headerName: "Statut",
      minWidth: 130,
      renderCell: ({ value }) => (
        <Chip
          label={value === "en_cours" ? "En cours" : "Curatif en cours"}
          size="small"
          sx={styles.actionStatus(value)}
        />
      ),
    },
  ], []);

  const correctiveColumns = useMemo(() => [
    { field: "reference", flex: 1, headerName: "Référence NC", minWidth: 175 },
    { field: "processus", flex: 0.9, headerName: "Processus", minWidth: 140 },
    { field: "action_corrective", flex: 1.5, headerName: "Action corrective", minWidth: 230 },
    { field: "plan_action", flex: 1.5, headerName: "Plan d'action", minWidth: 230 },
    { field: "responsable_name", flex: 1, headerName: "Responsable", minWidth: 180 },
    {
      field: "avancement",
      headerName: "Avancement",
      minWidth: 110,
      renderCell: ({ value }) => `${Number(value) || 0} %`,
    },
    { field: "date_debut", headerName: "Début", minWidth: 110, renderCell: ({ value }) => formatDate(value) },
    { field: "date_fin", headerName: "Fin prévue", minWidth: 110, renderCell: ({ value }) => formatDate(value) },
    {
      field: "delai_jours",
      headerName: "Délai",
      minWidth: 90,
      renderCell: ({ value }) => Number.isFinite(Number(value)) ? `${Number(value)}j` : "—",
    },
  ], []);

  const dialogColumns = activeDetailType === "curative"
    ? curativeColumns
    : activeDetailType === "corrective"
      ? correctiveColumns
      : ncDetailColumns;

  const stats = summary?.stats || EMPTY_STATS;
  const hasFilters = Boolean(
    filters.date_debut ||
    filters.date_fin ||
    filters.processus_id ||
    !sameId(filters.direction_id, defaultDirectionId)
  );
  const goToRegistry = useCallback(() => navigate("/smq_nc/registre"), [navigate]);

  const updateFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const selectDirection = (_, option) => {
    setFilters((current) => {
      const directionId = option?.id ?? null;
      const processStillValid = !current.processus_id || processes.some((process) =>
        sameId(process.processus_id, current.processus_id) &&
        (!directionId || sameId(process.direction_id, directionId))
      );
      return {
        ...current,
        direction_id: directionId,
        processus_id: processStillValid ? current.processus_id : null,
      };
    });
  };

  const openDetailDialog = (type) => {
    setDialogPagination({ page: 0, pageSize: 10 });
    setDialogRows([]);
    setDialogRowCount(0);
    setDetailDialog(DETAIL_DIALOGS[type]);
  };

  const openActionsDialog = () => {
    setActionTab("curative");
    setDialogPagination({ page: 0, pageSize: 10 });
    setDialogRows([]);
    setDialogRowCount(0);
    setDetailDialog({ title: "Actions en cours", type: "actions" });
  };

  const closeDetailDialog = () => {
    dialogRequestIdRef.current += 1;
    setDetailDialog(null);
    setDialogError(null);
  };

  const exportDashboard = async () => {
    if (
      exporting ||
      dateError ||
      !summary ||
      !statusChartRef.current ||
      !processChartRef.current
    ) {
      return;
    }

    const requestId = exportRequestIdRef.current + 1;
    exportRequestIdRef.current = requestId;
    const filtersSnapshot = { ...queryFilters };
    const summarySnapshot = summary;
    const recentRowsSnapshot = [...recentRows];

    setExporting(true);
    setExportError(null);

    try {
      const captureOptions = {
        backgroundColor: "#ffffff",
        logging: false,
        scale: 2,
        useCORS: true,
      };
      const [detailGroups, statusCanvas, processCanvas] = await Promise.all([
        fetchDashboardExportDetails(filtersSnapshot),
        html2canvas(statusChartRef.current, captureOptions),
        html2canvas(processChartRef.current, captureOptions),
      ]);

      if (!mountedRef.current || exportRequestIdRef.current !== requestId) return;

      await saveDashboardWorkbook({
        chartImages: {
          process: processCanvas.toDataURL("image/png"),
          status: statusCanvas.toDataURL("image/png"),
        },
        detailGroups,
        filterLabels: {
          date_debut: filters.date_debut ? formatDate(filters.date_debut) : "Tous",
          date_fin: filters.date_fin ? formatDate(filters.date_fin) : "Tous",
          direction: selectedDirection?.label || "Toutes les directions",
          processus: selectedProcess?.label || "Tous les processus",
        },
        recentRows: recentRowsSnapshot,
        summary: summarySnapshot,
      });
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.error("Dashboard export failed", error);
      }
      if (mountedRef.current && exportRequestIdRef.current === requestId) {
        setExportError(DASHBOARD_ERROR_MESSAGES.export);
      }
    } finally {
      if (mountedRef.current && exportRequestIdRef.current === requestId) {
        setExporting(false);
      }
    }
  };

  return (
    <SMQMainContainer title="Tableau de bord SMQ / NC">
      <Box sx={styles.dashboard}>
        <Paper elevation={0} sx={styles.filterBar}>
        <Grid container spacing={1} sx={{ alignItems: "center" }}>
          <Grid item xs={12} sm={6} md={6} lg={6}>
          <Typography color="#52677c" fontSize={10} fontWeight={700} letterSpacing="0.05em">FILTRES :</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                gap: 1,
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <Button
                disabled={!hasFilters || exporting}
                onClick={() => setFilters(createInitialFilters(defaultDirectionId))}
                size="small"
                startIcon={<RestartAltIcon />}
                sx={{ ...styles.resetButton, width: "auto" }}
                variant="outlined"
              >
                Réinitialiser
              </Button>
              <Button
                disabled={Boolean(
                  dateError ||
                  exporting ||
                  loading.summary ||
                  loading.recent ||
                  errors.summary ||
                  errors.recent ||
                  !summary
                )}
                onClick={exportDashboard}
                size="small"
                startIcon={exporting
                  ? <CircularProgress color="inherit" size={14} />
                  : <DownloadOutlinedIcon />}
                sx={styles.exportButton}
                variant="contained"
              >
                {exporting ? "Export en cours…" : "Exporter Excel"}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4} md={4} lg={4}>
          <Autocomplete
            disabled={exporting}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
            onChange={(_, option) => updateFilter("processus_id", option?.id ?? null)}
            options={processOptions}
            renderInput={(params) => <TextField {...params} placeholder="Tous les processus" size="small" label="Processus" />}
            sx={styles.filterAutocomplete}
            value={selectedProcess}
          />
          </Grid>
          <Grid item xs={12} sm={4} md={4} lg={4}>
          <Autocomplete
            disabled={exporting}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
            onChange={selectDirection}
            options={directionOptions}
            renderInput={(params) => <TextField {...params} placeholder="Toutes les directions" size="small" label="Direction" />}
            sx={styles.filterAutocomplete}
            value={selectedDirection}
          />
          </Grid>
          <Grid item xs={12} sm={2} md={2} lg={2}>
          <TextField
            InputLabelProps={{ shrink: true }}
            error={Boolean(dateError)}
            disabled={exporting}
            fullWidth
            label="Du"
            onChange={(event) => updateFilter("date_debut", event.target.value)}
            size="small"
            sx={styles.dateFilter}
            type="date"
            value={filters.date_debut}
          />
          </Grid>
          <Grid item xs={12} sm={2} md={2} lg={2}>
          <TextField
            InputLabelProps={{ shrink: true }}
            error={Boolean(dateError)}
            disabled={exporting}
            fullWidth
            label="Au"
            onChange={(event) => updateFilter("date_fin", event.target.value)}
            size="small"
            sx={styles.dateFilter}
            type="date"
            value={filters.date_fin}
          />
          </Grid>
        </Grid>
        </Paper>

        {dateError && <Alert severity="error" sx={styles.alert}>{dateError}</Alert>}
        {exportError && (
          <Alert
            action={<Button color="inherit" disabled={exporting} onClick={exportDashboard} size="small">Réessayer</Button>}
            onClose={() => setExportError(null)}
            severity="error"
            sx={styles.alert}
          >
            {exportError}
          </Alert>
        )}
        {optionsError && (
          <Alert action={<Button color="inherit" onClick={loadOptions} size="small">Réessayer</Button>} severity="error" sx={styles.alert}>
            {optionsError}
          </Alert>
        )}
        {directionError && (
          <Alert
            action={(
              <Button
                color="inherit"
                disabled={directionLoading}
                onClick={loadUserDirection}
                size="small"
              >
                {"R\u00e9essayer"}
              </Button>
            )}
            severity="error"
            sx={styles.alert}
          >
            {directionError}
          </Alert>
        )}

        {loading.overdue && !overduePreview.rows.length ? (
          <Skeleton height={76} sx={styles.overdueSkeleton} variant="rounded" />
        ) : errors.overdue ? (
          <Alert action={<Button color="inherit" onClick={loadOverduePreview} size="small">Réessayer</Button>} severity="error" sx={styles.overdueAlert}>
            {errors.overdue}
          </Alert>
        ) : overduePreview.total > 0 && (
          <Alert
            action={<Button color="inherit" onClick={() => openDetailDialog("expired")} size="small">Voir plus</Button>}
            icon={<WarningAmberOutlinedIcon />}
            severity="error"
            sx={styles.overdueAlert}
          >
            <Typography fontSize={12} fontWeight={800}>{overduePreview.total} NC avec délai dépassé</Typography>
            <Typography fontSize={11} sx={styles.overdueDetails}>
              {overduePreview.rows.map((row) => `${row.reference} (délai : ${formatDate(row.delai)})`).join(" · ")}
            </Typography>
          </Alert>
        )}

        {errors.summary && (
          <Alert action={<Button color="inherit" onClick={loadSummary} size="small">Réessayer</Button>} severity="error" sx={styles.alert}>
            {errors.summary}
          </Alert>
        )}

        {loading.summary && !summary ? <DashboardSkeleton /> : (
          <>
            <Box sx={styles.kpiGrid}>
              <KpiCard accent={COLORS.navy} helper="Total cumulé" helperIcon={AssignmentOutlinedIcon} label="NC enregistrées" onClick={goToRegistry} value={stats.total} />
              <KpiCard accent={COLORS.green} helper="Suivi terminé" helperIcon={CheckCircleOutlineIcon} label="NC clôturées" onClick={() => openDetailDialog("closed")} value={stats.closed} />
              <ActionsKpiCard correctives={stats.correctives_in_progress} curatifs={stats.curatifs_in_progress} onClick={openActionsDialog} />
              <KpiCard accent={COLORS.red} helper="Statut ouvert" helperIcon={PendingActionsOutlinedIcon} label="NC non traitées" onClick={() => openDetailDialog("open")} value={stats.open} />
              <KpiCard accent={COLORS.orange} helper="À traiter rapidement" helperIcon={HourglassEmptyOutlinedIcon} label="Échéance dans 3 j" onClick={() => openDetailDialog("due_soon")} value={stats.due_soon} />
              <KpiCard accent={COLORS.red} helper="Action requise" helperIcon={WarningAmberOutlinedIcon} label="Délais dépassés" onClick={() => openDetailDialog("expired")} value={stats.expired} />
            </Box>
            <Box sx={styles.chartsGrid}>
              <Box ref={statusChartRef} sx={styles.chartCapture}>
                <ChartCard icon={DonutLargeOutlinedIcon} title="Répartition par statut">
                  <StatusChart distribution={summary?.status_distribution || []} total={stats.total} />
                </ChartCard>
              </Box>
              <Box ref={processChartRef} sx={styles.chartCapture}>
                <ChartCard icon={BarChartOutlinedIcon} title="NC par processus">
                  <ProcessChart data={summary?.process_distribution || []} />
                </ChartCard>
              </Box>
            </Box>
          </>
        )}

        <Paper elevation={0} sx={styles.latestCard}>
          <Box sx={styles.latestHeader}>
            <FactCheckOutlinedIcon sx={styles.tableIcon} />
            <Box>
              <Typography color={COLORS.navy} fontSize={13.5} fontWeight={800}>Dernières NC enregistrées</Typography>
              <Typography color="#60758a" fontSize={10.5}>10 plus récentes</Typography>
            </Box>
            <Button endIcon={<ArrowForwardIcon />} onClick={goToRegistry} size="small" sx={styles.viewAllButton} variant="outlined">Voir tout</Button>
          </Box>
          {errors.recent && (
            <Alert action={<Button color="inherit" onClick={loadRecent} size="small">Réessayer</Button>} severity="error" sx={styles.sectionError}>
              {errors.recent}
            </Alert>
          )}
          <Box sx={styles.dataGridContainer}>
            <DataGrid
              columns={columns}
              disableRowSelectionOnClick
              hideFooter
              loading={loading.recent}
              localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
              onCellKeyDown={(_, event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  goToRegistry();
                }
              }}
              onRowClick={goToRegistry}
              rowHeight={48}
              rows={recentRows}
              sx={styles.dataGrid}
            />
          </Box>
        </Paper>

        <Paper elevation={0} sx={styles.guideCard}>
          <Box sx={styles.guideHeader}>
            <Box sx={styles.guideHeaderIcon}><MenuBookOutlinedIcon sx={styles.guideIcon} /></Box>
            <Box>
              <Typography color={COLORS.navy} fontSize={13.5} fontWeight={800}>Guide rapide — Les 5 étapes de traitement d&apos;une NC + Suivi efficacité</Typography>
              <Typography color="#60758a" fontSize={10.5}>Rappel méthodologique ISO 9001</Typography>
            </Box>
          </Box>
          <Box sx={styles.guideStepGrid}>
            {GUIDE_STEPS.map((step, index) => {
              const Icon = step.icon;
              return (
                <Box key={step.label} sx={styles.guideStep(step.color)}>
                  <Icon sx={styles.guideStepIcon} />
                  <Typography color="inherit" fontSize={17} fontWeight={900} lineHeight={1.15} sx={styles.guideStepNumber}>{index + 1}</Typography>
                  <Typography color="inherit" fontSize={9.5} fontWeight={800} sx={styles.guideStepTitle}>{step.label}</Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>

        <Dialog fullWidth maxWidth="xl" onClose={closeDetailDialog} open={Boolean(detailDialog)}>
          <DialogTitle sx={styles.dialogTitle}>{detailDialog?.title}</DialogTitle>
          {detailDialog?.type === "actions" && (
            <Tabs
              aria-label="Type d'actions en cours"
              onChange={(_, value) => setActionTab(value)}
              sx={styles.dialogTabs}
              value={actionTab}
            >
              <Tab label={`Curatifs en cours (${stats.curatifs_in_progress})`} sx={styles.dialogTab} value="curative" />
              <Tab label={`Correctives en cours (${stats.correctives_in_progress})`} sx={styles.dialogTab} value="corrective" />
            </Tabs>
          )}
          <DialogContent dividers>
            {dialogError && (
              <Alert action={<Button color="inherit" onClick={loadDialogRows} size="small">Réessayer</Button>} severity="error" sx={styles.alert}>
                {dialogError}
              </Alert>
            )}
            <Box sx={styles.dialogGrid}>
              <DataGrid
                columns={dialogColumns}
                disableRowSelectionOnClick
                loading={dialogLoading}
                localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                onPaginationModelChange={setDialogPagination}
                pageSizeOptions={[10, 25, 50]}
                paginationMode="server"
                paginationModel={dialogPagination}
                rowCount={dialogRowCount}
                rowHeight={48}
                rows={dialogRows}
                sx={styles.dataGrid}
              />
            </Box>
          </DialogContent>
          <DialogActions><Button onClick={closeDetailDialog}>Fermer</Button></DialogActions>
        </Dialog>
      </Box>
    </SMQMainContainer>
  );
};

export default Dashboard;
